import { createContext, useContext, useReducer, useMemo } from "react";
import PropTypes from "prop-types";

// Main context
const Argon = createContext(null);
Argon.displayName = "ArgonContext";

// Reducer
function reducer(state, action) {
  switch (action.type) {
    case "MINI_SIDENAV": return { ...state, miniSidenav: action.value };
    case "DARK_SIDENAV": return { ...state, darkSidenav: action.value };
    case "SIDENAV_COLOR": return { ...state, sidenavColor: action.value };
    case "TRANSPARENT_NAVBAR": return { ...state, transparentNavbar: action.value };
    case "FIXED_NAVBAR": return { ...state, fixedNavbar: action.value };
    case "OPEN_CONFIGURATOR": return { ...state, openConfigurator: action.value };
    case "LAYOUT": return { ...state, layout: action.value };
    case "DARK_MODE": return { ...state, darkMode: action.value };
    default: throw new Error(`Unhandled action type: ${action.type}`);
  }
}

// Provider
function ArgonControllerProvider({ children }) {
  const initialState = {
    miniSidenav: false,
    darkSidenav: false,
    sidenavColor: "primary", // ✅ FIXED: prevent crash on undefined color
    transparentNavbar: true,
    fixedNavbar: false,
    openConfigurator: false,
    layout: "dashboard",
    darkMode: false,
  };

  const [controller, dispatch] = useReducer(reducer, initialState);
  const value = useMemo(() => [controller, dispatch], [controller, dispatch]);

  return <Argon.Provider value={value}>{children}</Argon.Provider>;
}

// Hook
function useArgonController() {
  const context = useContext(Argon);
  if (!context) {
    throw new Error("useArgonController must be used inside ArgonControllerProvider.");
  }
  return context;
}

// Actions
const setMiniSidenav = (dispatch, value) => dispatch({ type: "MINI_SIDENAV", value });
const setDarkSidenav = (dispatch, value) => dispatch({ type: "DARK_SIDENAV", value });
const setSidenavColor = (dispatch, value) => dispatch({ type: "SIDENAV_COLOR", value });
const setTransparentNavbar = (dispatch, value) => dispatch({ type: "TRANSPARENT_NAVBAR", value });
const setFixedNavbar = (dispatch, value) => dispatch({ type: "FIXED_NAVBAR", value });
const setOpenConfigurator = (dispatch, value) => dispatch({ type: "OPEN_CONFIGURATOR", value });
const setLayout = (dispatch, value) => dispatch({ type: "LAYOUT", value });
const setDarkMode = (dispatch, value) => dispatch({ type: "DARK_MODE", value });

ArgonControllerProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export {
  ArgonControllerProvider,
  useArgonController,
  setMiniSidenav,
  setDarkSidenav,
  setSidenavColor,
  setTransparentNavbar,
  setFixedNavbar,
  setOpenConfigurator,
  setLayout,
  setDarkMode,
};
